import { useEffect, useState } from "react";
import { Gift, Check, X, Clock, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { AdminHeader, TableWrap, Th, Td, Tr, Spinner, EmptyRow } from "@/components/admin/ui";

const statusPill = (s) => {
  if (s === "approved") return { color: "#00ff9d", bg: "rgba(0,255,157,0.14)" };
  if (s === "denied") return { color: "#ff2e97", bg: "rgba(255,46,151,0.14)" };
  return { color: "#ffb020", bg: "rgba(255,176,32,0.14)" };
};

export default function AdminGiftCardRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");

  const load = () => {
    setLoading(true);
    const q = statusFilter && statusFilter !== "all" ? `?status=${statusFilter}` : "";
    apiClient.get(`/admin/gift-card-requests${q}`)
      .then(({ data }) => setRows(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]);

  const approve = async (r) => {
    const code = window.prompt(`Approving $${r.amount.toFixed(2)} for ${r.user_name}. Optional gift-card code:`) ?? "";
    try {
      await apiClient.post(`/admin/gift-card-requests/${r.id}/approve`, { gift_card_code: code.trim() || null });
      toast.success("Request approved");
      load();
    } catch (err) { toast.error(getErrorMessage(err, "Approve failed")); }
  };

  const deny = async (r) => {
    const note = window.prompt(`Denying $${r.amount.toFixed(2)} for ${r.user_name}. Reason (optional — shared with user):`) ?? "";
    if (!window.confirm(`Deny and refund $${r.amount.toFixed(2)} to their cash credit?`)) return;
    try {
      await apiClient.post(`/admin/gift-card-requests/${r.id}/deny`, { note: note.trim() || null });
      toast.success("Request denied — credit refunded");
      load();
    } catch (err) { toast.error(getErrorMessage(err, "Deny failed")); }
  };

  const pending = rows.filter((r) => r.status === "pending").length;

  return (
    <div>
      <AdminHeader
        title="Gift-card Requests"
        subtitle={`Approve or deny cash-credit → gift-card conversions${pending ? ` · ${pending} pending` : ""}`}
        right={
          <div className="flex gap-2">
            {["pending", "approved", "denied", "all"].map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} className="chip"
                data-testid={`gift-filter-${s}`}
                style={statusFilter === s
                  ? { background: "rgba(0,229,255,0.14)", border: "1px solid rgba(0,229,255,0.5)", color: "#00e5ff" }
                  : { background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)", color: "#94a3b8" }}>
                {s.toUpperCase()}
              </button>
            ))}
          </div>
        }
      />
      {loading ? <Spinner label="Loading requests" /> : (
        <TableWrap>
          <thead>
            <Tr>
              <Th>Requested</Th>
              <Th>User</Th>
              <Th>Amount</Th>
              <Th>Status</Th>
              <Th>Resolved</Th>
              <Th></Th>
            </Tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <EmptyRow colSpan={6} text={statusFilter === "pending" ? "No pending gift-card requests." : "No requests match this filter."} />
            ) : rows.map((r) => {
              const s = statusPill(r.status);
              return (
                <Tr key={r.id}>
                  <Td className="text-slate-400">
                    <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-slate-500" /> {new Date(r.created_at).toLocaleString()}</div>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-4 h-4 text-slate-500" />
                      <div>
                        <p className="text-white font-semibold">{r.user_name || "—"}</p>
                        <p className="text-slate-500 text-xs">{r.user_email}</p>
                      </div>
                    </div>
                  </Td>
                  <Td><span className="font-display font-bold neon-cyan-text">${r.amount.toFixed(2)}</span></Td>
                  <Td><span className="chip capitalize" style={{ background: s.bg, border: `1px solid ${s.color}66`, color: s.color }}>{r.status}</span></Td>
                  <Td className="text-slate-500 text-xs">
                    {r.resolved_at ? (
                      <>
                        {new Date(r.resolved_at).toLocaleDateString()}
                        {r.gift_card_code && <p className="text-slate-400 mt-1 font-mono">Code: {r.gift_card_code}</p>}
                        {r.resolved_note && <p className="text-slate-500 mt-1 italic">&ldquo;{r.resolved_note}&rdquo;</p>}
                      </>
                    ) : "—"}
                  </Td>
                  <Td>
                    {r.status === "pending" ? (
                      <div className="flex gap-2">
                        <button onClick={() => approve(r)} className="chip" style={{ background: "rgba(0,255,157,0.14)", border: "1px solid rgba(0,255,157,0.45)", color: "#00ff9d" }} data-testid={`approve-${r.id}`}>
                          <Check className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button onClick={() => deny(r)} className="chip" style={{ background: "rgba(255,46,151,0.14)", border: "1px solid rgba(255,46,151,0.45)", color: "#ff2e97" }} data-testid={`deny-${r.id}`}>
                          <X className="w-3.5 h-3.5" /> Deny
                        </button>
                      </div>
                    ) : (
                      <Gift className="w-4 h-4 text-slate-600 mx-auto" />
                    )}
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </TableWrap>
      )}
    </div>
  );
}
